import { useCallback, useEffect, useState } from "react";
import { brand } from "../../../config/theme";
import Modal from "../../../components/base/Modal";
import Swal from "sweetalert2";
import { buildThemeClasses } from "../theme";
import {
  fetchMetaStatus,
  metaWebhookUrl,
  backfillMetaLead,
  type MetaIntegrationStatus,
} from "../services/metaAdsService";

// ===========================================================================
// Meta Ads integration panel — the "Conectar Meta Ads" modal opened from the
// Leads section header. Shows whether the webhook is configured, the exact
// callback URL + verify-token reminder to paste into Meta's Webhook setup, and
// a manual import-by-leadgen_id box (for testing or migrating leads that are
// currently going to HubSpot).
//
// Live Meta leads arrive server-to-server (POST /crm/meta/webhooks/leadgen) and
// land in the same Leads table automatically — this panel is just the control
// surface for the connection.
// ===========================================================================

function Row({
  ok,
  label,
  hint,
  tc,
}: {
  ok: boolean;
  label: string;
  hint: string;
  tc: ReturnType<typeof buildThemeClasses>;
}) {
  return (
    <div className="flex items-start gap-2">
      <i
        className={
          ok
            ? "ri-checkbox-circle-fill mt-0.5 text-emerald-500"
            : "ri-error-warning-fill mt-0.5 text-amber-500"
        }
      />
      <div>
        <p className={`text-sm font-medium ${tc.text}`}>{label}</p>
        <p className={`text-xs ${tc.textSecondary}`}>{hint}</p>
      </div>
    </div>
  );
}

export function MetaAdsPanel({ onClose, onImported }: { onClose: () => void; onImported?: () => void }) {
  const tc = buildThemeClasses();

  const [status, setStatus] = useState<MetaIntegrationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [leadgenId, setLeadgenId] = useState("");
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setStatus(await fetchMetaStatus());
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setError(err.response?.data?.message || err.message || "No se pudo cargar el estado de la integración");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      Swal.fire({ icon: "success", title: "Copiado", timer: 1200, showConfirmButton: false });
    } catch {
      /* clipboard unavailable — no-op */
    }
  };

  const doImport = async () => {
    const id = leadgenId.trim();
    if (!id) return;
    setImporting(true);
    try {
      const res = await backfillMetaLead(id);
      if (res.created) {
        Swal.fire({ icon: "success", title: "Lead importado", text: "El lead de Meta Ads se agregó a la bandeja.", confirmButtonColor: brand.primary });
        setLeadgenId("");
        onImported?.();
      } else {
        Swal.fire({ icon: "info", title: "Ya existía", text: "Ese leadgen_id ya estaba importado.", confirmButtonColor: brand.primary });
      }
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      Swal.fire({ icon: "error", title: "No se pudo importar", text: err.response?.data?.message || err.message || "Revisa el leadgen_id y el token de la página.", confirmButtonColor: brand.primary });
    } finally {
      setImporting(false);
    }
  };

  const webhookUrl = status ? metaWebhookUrl(status) : "";

  return (
    <Modal
      title="Conectar Meta Ads"
      onClose={onClose}
      themeClasses={tc}
      content={
        <div className="space-y-5">
          {loading ? (
            <div className={`py-6 text-center text-sm ${tc.textSecondary}`}>
              <i className="ri-loader-4-line mr-2 animate-spin" />
              Cargando estado…
            </div>
          ) : error ? (
            <div className="py-4 text-center">
              <i className="ri-error-warning-line mb-2 text-2xl text-red-500" />
              <p className="text-sm text-red-500">{error}</p>
              <button onClick={load} className="mt-3 rounded-lg bg-primary-500 px-4 py-2 text-sm text-on-primary hover:bg-primary-400">
                Reintentar
              </button>
            </div>
          ) : status ? (
            <>
              {/* Overall connection state */}
              <div
                className={`flex items-center gap-3 rounded-xl border p-3 ${
                  status.configured
                    ? "border-emerald-500/40 bg-emerald-500/10"
                    : "border-amber-500/40 bg-amber-500/10"
                }`}
              >
                <i
                  className={
                    status.configured
                      ? "ri-plug-fill text-2xl text-emerald-500"
                      : "ri-plug-line text-2xl text-amber-500"
                  }
                />
                <div>
                  <p className={`text-sm font-semibold ${tc.text}`}>
                    {status.configured ? "Integración lista" : "Falta configuración"}
                  </p>
                  <p className={`text-xs ${tc.textSecondary}`}>
                    {status.configured
                      ? "Los leads de tus campañas de Meta llegarán a esta bandeja automáticamente."
                      : "Completa las variables en el backend (.env) para empezar a recibir leads."}
                  </p>
                </div>
              </div>

              {/* Config checklist */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Row ok={status.appSecretSet} label="App Secret" hint="META_APP_SECRET — verifica la firma del webhook." tc={tc} />
                <Row ok={status.verifyTokenSet} label="Verify Token" hint="META_VERIFY_TOKEN — para el handshake de suscripción." tc={tc} />
                <Row ok={status.pageTokenSet} label="Page Access Token" hint="META_PAGE_ACCESS_TOKEN — lee los datos del lead." tc={tc} />
                <Row ok label="Graph API" hint={`Versión ${status.graphVersion}.`} tc={tc} />
              </div>

              {/* Webhook URL to paste into Meta */}
              <div className={`rounded-xl border p-3 ${tc.border}`}>
                <p className={`mb-1 text-xs font-medium ${tc.textSecondary}`}>
                  URL de devolución de llamada (Webhook) — pégala en Meta → App → Webhooks → Page → campo <b>leadgen</b>
                </p>
                <div className="flex items-center gap-2">
                  <code className={`flex-1 truncate rounded-lg bg-black/5 px-2 py-1.5 text-xs dark:bg-white/10 ${tc.text}`}>
                    {webhookUrl}
                  </code>
                  <button
                    onClick={() => copy(webhookUrl)}
                    className="rounded-lg border px-3 py-1.5 text-xs hover:bg-primary-500/10"
                    title="Copiar URL"
                  >
                    <i className="ri-file-copy-line" />
                  </button>
                </div>
                <p className={`mt-2 text-xs ${tc.textSecondary}`}>
                  Usa el mismo <b>Verify Token</b> que pusiste en <code>META_VERIFY_TOKEN</code> al verificar la suscripción.
                </p>
              </div>

              {/* Manual import / migration */}
              <div className={`rounded-xl border p-3 ${tc.border}`}>
                <p className={`mb-1 text-sm font-medium ${tc.text}`}>Importar un lead manualmente</p>
                <p className={`mb-2 text-xs ${tc.textSecondary}`}>
                  Pega un <code>leadgen_id</code> de Meta para importarlo ahora (útil para probar o migrar leads sueltos).
                </p>
                <div className="flex items-center gap-2">
                  <input
                    value={leadgenId}
                    onChange={(e) => setLeadgenId(e.target.value)}
                    placeholder="leadgen_id"
                    className="flex-1 rounded-lg border px-3 py-2 text-sm outline-none border-line bg-app text-content"
                  />
                  <button
                    onClick={doImport}
                    disabled={importing || !leadgenId.trim()}
                    className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary transition-colors hover:bg-primary-400 disabled:opacity-50"
                  >
                    <i className={importing ? "ri-loader-4-line animate-spin" : "ri-download-2-line"} />
                    Importar
                  </button>
                </div>
              </div>
            </>
          ) : null}
        </div>
      }
    />
  );
}
